import React, { type FC, useEffect } from 'react';
import {
  DataTable,
  type DataTableStateEvent,
  type DataTableRowClickEvent,
} from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import {
  type PeriodGroupItem,
  type PeriodItem,
  PeriodType,
  TimePeriodOptions,
} from '../../models/Period';
import { formatMileage, getPeriodEnum, noop } from '../../utils/GeneralUtils';
import DateUtils from '../../utils/DateTimeUtils';
import type { ITrip } from '../../models/Trip';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import UserSearch from '../../components/UserSearch';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useTenantStore, { selectTenantId } from '../../stores/TenantStore';
import OpenAPI from '../../endpoint/Openapi';
import { type paths } from '../../endpoint/Schema';
import { useDemoMode } from '../../contexts/DemoModeContext';
import { maskEmail, maskName, maskAddress } from '../../utils/piiMasking';

dayjs.extend(duration);
const ROWS = 10;

type TableParams = Pick<DataTableStateEvent, 'first' | 'rows'>;

const Trips: FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isDemoModeActive } = useDemoMode();
  const [userId, setUserId] = React.useState<string | undefined>(
    () => searchParams.get('userId') ?? undefined,
  );
  useEffect(() => {
    setLazyParams({
      first: +(searchParams.get('first') ?? '0'),
      rows: ROWS,
    });

    setUserId(searchParams.get('userId') ?? '');
  }, [searchParams]);

  const tenantId = useTenantStore(selectTenantId);
  const [selectedPeriod, setSelectedPeriod] = React.useState<PeriodType>(() => {
    const periodParam = searchParams.get('period');
    const enumValue = PeriodType[periodParam as keyof typeof PeriodType];
    return enumValue ?? PeriodType.LAST_7_DAY;
  });

  const [lazyParams, setLazyParams] = React.useState<TableParams>({
    first: 0,
    rows: ROWS,
  });
  const [loading, setLoading] = React.useState(false);
  const [data, setData] =
    React.useState<
      paths['/api/v2/admin/trips/all']['get']['responses']['200']['content']['application/json']
    >();

  React.useEffect(() => {
    const getTrips = async () => {
      setLoading(true);

      try {
        const res = await OpenAPI.GET('/api/v2/admin/trips/all', {
          params: {
            query: {
              tenantId,
              period: getPeriodEnum(selectedPeriod),
              skip: lazyParams.first,
              take: lazyParams.rows,
              userId,
            },
          },
        });

        setData(res.data);
      } catch {}

      setLoading(false);
    };

    getTrips().catch(noop);
  }, [lazyParams.first, lazyParams.rows, selectedPeriod, tenantId, userId]);

  function onSelectedPeriodChange(event: { value: PeriodType }): void {
    const o = {
      first: lazyParams.first.toString(),
      rows: lazyParams.rows.toString(),
      userId: searchParams.get('userId')?.toString() ?? '',
      period: event.value.toString(),
    };
    setSearchParams(o);
    setSelectedPeriod(event.value);
  }

  const groupedItemTemplate = (option: PeriodGroupItem): JSX.Element => {
    return (
      <div className="flex align-items-center text-xl">
        <div>{option.label}</div>
      </div>
    );
  };

  const periodItemTemplate = (option: PeriodItem): JSX.Element => {
    return (
      <div className="pl-6 text-xl">
        <div>{option.label}</div>
      </div>
    );
  };

  function handlePageChange(event: DataTableStateEvent) {
    const params = event as unknown as TableParams;
    const o = {
      first: params.first.toString(),
      rows: params.rows.toString(),
      userId: searchParams.get('userId')?.toString() ?? '',
      period:
        searchParams.get('period')?.toString() ??
        PeriodType.LAST_7_DAY.toString(),
    };

    setSearchParams(o);
  }

  function startDateBodyTemplate(rowData: ITrip) {
    return DateUtils.formatDateTime(rowData.startDate);
  }

  function endDateBodyTemplate(rowData: ITrip) {
    return DateUtils.formatDateTime(rowData.endDate);
  }

  function durationBodyTemplate(rowData: ITrip) {
    return dayjs.duration(rowData.duration, 'minutes').format('HH:mm:ss');
  }

  function scoreBodyTemplate(rowData: ITrip) {
    return rowData.score ? Math.round(rowData.score) : '-';
  }

  function accelerationScoreBodyTemplate(rowData: ITrip) {
    return rowData.accelerationScore
      ? Math.round(rowData.accelerationScore)
      : '-';
  }

  function brakingScoreBodyTemplate(rowData: ITrip) {
    return rowData.brakingScore ? Math.round(rowData.brakingScore) : '-';
  }

  function corneringScoreBodyTemplate(rowData: ITrip) {
    return rowData.corneringScore ? Math.round(rowData.corneringScore) : '-';
  }

  function speedingScoreBodyTemplate(rowData: ITrip) {
    return rowData.speedingScore ? Math.round(rowData.speedingScore) : '-';
  }

  function phoneUsageScoreBodyTemplate(rowData: ITrip) {
    return rowData.phoneUsageScore ? Math.round(rowData.phoneUsageScore) : '-';
  }

  function mileageBodyTemplate(rowData: ITrip) {
    return formatMileage(rowData.mileage);
  }

  function userBodyTemplate(rowData: ITrip) {
    if (isDemoModeActive) {
      if (rowData.userName) return maskName(rowData.userName);
      if (rowData.userEmail) return maskEmail(rowData.userEmail);
      return 'N/A';
    }
    return rowData.userName ?? rowData.userEmail ?? 'N/A';
  }

  function startPointBodyTemplate(rowData: ITrip) {
    if (isDemoModeActive) {
      return maskAddress(rowData.startPoint);
    }
    return rowData.startPoint;
  }

  function endPointBodyTemplate(rowData: ITrip) {
    if (isDemoModeActive) {
      return maskAddress(rowData.endPoint);
    }
    return rowData.endPoint;
  }

  function onRowClick(event: DataTableRowClickEvent) {
    const tripData = event.data as ITrip;
    const tripId = tripData.id;
    const tripUserId = tripData.userId;
    if (tripUserId && tripId) {
      navigate(`/user/${tripUserId}/trip/${tripId}`);
    }
  }

  return (
    <div className="w-full">
      <div>
        <div>Time period</div>
        <Dropdown
          options={TimePeriodOptions}
          value={selectedPeriod}
          onChange={onSelectedPeriodChange}
          optionLabel="label"
          optionGroupLabel="label"
          optionGroupChildren="items"
          placeholder="Last 7 days"
          className="p-2 min-w-[300px]"
          optionGroupTemplate={groupedItemTemplate}
          itemTemplate={periodItemTemplate}
        />
      </div>

      <br />
      <UserSearch
        tenantId={tenantId}
        handleNewUserSelected={(e) => {
          navigate(`/trips?userId=${e.id}&period=${selectedPeriod}`);
        }}
        handleInputOnChange={(e) => {
          if (!e) navigate(`/trips`);
        }}
      />

      <br />

      <DataTable
        value={data?.page ?? []}
        dataKey="id"
        scrollable
        paginator
        rows={ROWS}
        first={lazyParams.first}
        totalRecords={data?.count ?? 0}
        loading={loading}
        rowHover
        lazy
        onPage={handlePageChange}
        filterDisplay="row"
        selectionMode="single"
        onRowClick={onRowClick}
        style={{ cursor: 'pointer' }}
      >
        <Column field="userName" header="User" body={userBodyTemplate} />
        <Column field="transportType" header="Role" />
        <Column
          field="duration"
          header="Duration"
          body={durationBodyTemplate}
        />
        <Column field="mileage" header="Mileage" body={mileageBodyTemplate} />
        <Column
          field="startPoint"
          header="Start Point"
          body={startPointBodyTemplate}
        />
        <Column
          field="endPoint"
          header="End Point"
          body={endPointBodyTemplate}
        />
        <Column
          field="startDate"
          header="Start Time"
          body={startDateBodyTemplate}
        />
        <Column field="endDate" header="End Time" body={endDateBodyTemplate} />
        <Column field="score" header="Safety Score" body={scoreBodyTemplate} />
        <Column
          field="accelerationScore"
          header="Acceleration"
          body={accelerationScoreBodyTemplate}
        />
        <Column
          field="brakingScore"
          header="Braking"
          body={brakingScoreBodyTemplate}
        />
        <Column
          field="corneringScore"
          header="Cornering"
          body={corneringScoreBodyTemplate}
        />
        <Column
          field="speedingScore"
          header="Speeding"
          body={speedingScoreBodyTemplate}
        />
        <Column
          field="phoneUsageScore"
          header="Phone Usage"
          body={phoneUsageScoreBodyTemplate}
        />
      </DataTable>
    </div>
  );
};

export default Trips;
